import { Box, IconButton, Typography } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { ReactElement } from "react"
import { useEffect, useState } from "react"
import { useFormContext } from "react-hook-form"

import useCompetitorProducts from "../hooks/useCompetitorProducts"

import type { CompetitorProductFormValues } from "./competitorProductSchema"

import { AddDataRow } from "@/components/common/AddDataRow"
import ValidationAdornment from "@/components/common/Autocomplete/ValidationAdornment"
import { FormMultiAutocomplete } from "@/components/UiKit/FormControls/FormMultiAutocomplete"
import { FormTextField } from "@/components/UiKit/FormControls/FormTextField"
import FileArea from "@/components/upload/components/FileArea"
import { DOCUMENT_UPLOAD_MIME_TYPES, DOCUMENT_UPLOAD_TYPES_LABEL } from "@/components/upload/constants"
import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import { FIELD_ERROR_CLASSES } from "@/constants/forms"
import { ReferenceTableName } from "@/graphql/codegen/graphql"
import { useDistributorOptions } from "@/hooks/useDistributorOptions"
import useDuplicateByFieldValidation from "@/hooks/useDuplicateByFieldValidation"
import useDebounce from "@/utils/useDebounce"

interface CompetitorProductFormProps {
  onMarkFileAsNeedRemoval?: (fileId: string) => void
}

const CompetitorProductForm = ({ onMarkFileAsNeedRemoval }: CompetitorProductFormProps = {}): ReactElement => {
  const {
    control,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useFormContext<CompetitorProductFormValues>()
  const formCasNumbers = watch("casNumbers")
  const competitorProductId = watch("competitorProductId")
  const [casCount, setCasCount] = useState(1)
  const [query, setQuery] = useState("")
  const debouncedName = useDebounce(query, DEBOUNCE_DELAY_MS)
  const shouldQuery = debouncedName.length >= 2

  // Update casCount when form data loads (for edit mode)
  useEffect(() => {
    if (formCasNumbers && formCasNumbers.length > 0) {
      setCasCount(formCasNumbers.length)
    }
  }, [formCasNumbers])

  const { competitorProductsList, competitorProductsListFetching } = useCompetitorProducts({
    query: debouncedName,
    pause: !shouldQuery,
  })

  const { distributorOptions, isLoadingDistributors } = useDistributorOptions()

  useDuplicateByFieldValidation({
    query,
    list: competitorProductsList,
    fieldName: "productName",
    fieldLabel: "product name",
    entityName: "Competitor product",
  })

  const handleProductNameChange = (value: string) => {
    setQuery(value)
  }

  const handleAddCAS = () => {
    setCasCount((prev) => prev + 1)
  }

  const handleRemoveCAS = (index: number) => {
    if (casCount > 1) {
      setCasCount((prev) => prev - 1)
      // Reset the removed CAS field
      setValue(`casNumbers.${index}` as any, "")
    }
  }

  return (
    <Box className='space-y-6'>
      <FormTextField
        control={control}
        name='productName'
        label='New Product Name'
        placeholder='Product Name'
        required
        onChange={handleProductNameChange}
        slotProps={{
          input: {
            endAdornment: query ? (
              <ValidationAdornment loading={competitorProductsListFetching} error={errors.productName} />
            ) : undefined,
          },
        }}
        data-testid='competitor-product-name-input'
      />
      <FormTextField
        control={control}
        name='chemicalName'
        label='Chemical Name of Product'
        placeholder='Chemical Name'
        data-testid='competitor-chemical-name-input'
      />

      <Box>
        <Box className='mb-2 flex items-center gap-2'>
          <Typography variant='body2' className='text-gray-700'>
            CAS Product Number
          </Typography>
        </Box>

        {Array.from({ length: casCount }).map((_, index) => (
          <Box key={index} className='mb-3 flex items-start gap-2'>
            <FormTextField
              control={control}
              name={`casNumbers.${index}` as any}
              placeholder='CAS #'
              onChange={() => trigger("casNumbers")}
              data-testid={`competitor-cas-number-${index}-input`}
            />
            {index > 0 && (
              <IconButton
                size='small'
                onClick={() => handleRemoveCAS(index)}
                className='mt-2 text-gray-500'
                data-testid={`competitor-cas-number-${index}-remove-button`}
              >
                <CloseOutlined fontSize='small' />
              </IconButton>
            )}
          </Box>
        ))}
        {errors.casNumbers?.message && <p className={FIELD_ERROR_CLASSES}>{errors.casNumbers.message}</p>}
        <AddDataRow label='Additional CAS' onClick={handleAddCAS} data-testid='competitor-add-cas-button' />
      </Box>

      <FormTextField
        control={control}
        name='manufacturer'
        label='Name of Product Manufacturer'
        placeholder='Manufacturer Name'
        data-testid='competitor-manufacturer-input'
      />

      <FormMultiAutocomplete
        control={control}
        name='distributors'
        label='Name of Product Distributor(s)'
        placeholder='Search and select distributors'
        options={distributorOptions}
        disabled={isLoadingDistributors}
        data-testid='competitor-distributors-field'
      />

      <Box data-testid='competitor-technical-file-field'>
        <FileArea<CompetitorProductFormValues>
          name='technicalFile'
          title='Technical Specifications Document'
          allowedMimeTypes={DOCUMENT_UPLOAD_MIME_TYPES}
          allowedTypesLabel={DOCUMENT_UPLOAD_TYPES_LABEL}
          referenceId={competitorProductId}
          referenceTableName={ReferenceTableName.CompetitorProduct}
          onMarkFileAsNeedRemoval={onMarkFileAsNeedRemoval}
          showUploadAreaByDefault
        />
      </Box>
    </Box>
  )
}

export default CompetitorProductForm
